import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Check, Loader2, Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BuyCreditsModal = ({ open, onClose }) => {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const { refreshUser } = useAuth();

  const fetchBundles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/credits/bundles`);
      setBundles(response.data.bundles);
    } catch (error) {
      console.error('Error fetching bundles:', error);
      toast.error('Failed to load credit bundles');
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchBundles();
    }
  }, [open, fetchBundles]);

  const handlePurchase = async (bundleType) => {
    setLoading(true);
    setSelectedBundle(bundleType);

    try {
      // Get current origin for success/cancel URLs
      const origin = window.location.origin;
      const successUrl = `${origin}/payment-success`;
      const cancelUrl = `${origin}/settings`;

      // Initiate payment
      const response = await axios.post(`${API}/payments/initiate`, {
        bundle_type: bundleType,
        success_url: successUrl,
        cancel_url: cancelUrl
      });

      // Store transaction ID in sessionStorage
      sessionStorage.setItem('pending_transaction_id', response.data.transaction_id);

      // Redirect to Stripe payment page
      window.location.href = response.data.payment_url;
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error('Failed to initiate payment. Please try again.');
      setLoading(false);
      setSelectedBundle(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl" style={{ backgroundColor: '#FFFFFF', maxHeight: '90vh', overflowY: 'auto' }}>
        <DialogHeader>
          <DialogTitle style={{ color: '#245451', fontSize: '1.75rem', fontWeight: 700 }}>Purchase Credits</DialogTitle>
          <DialogDescription style={{ color: '#64748B', fontSize: '1rem', marginTop: '0.5rem' }}>
            Select a credit package to continue generating medical summaries.
            <span style={{ fontWeight: 600, color: '#C49603', display: 'block', marginTop: '0.25rem' }}>1 Credit = 1 Complete Medical Summary</span>
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-6">
          {bundles.map((bundle) => (
            <Card
              key={bundle.type}
              className="p-6 hover:shadow-lg transition-all cursor-pointer border-2 relative"
              style={{ 
                backgroundColor: '#FFFFFF',
                borderColor: bundle.type === '10' ? '#C49603' : '#E2E8F0',
                borderWidth: bundle.type === '10' ? '2px' : '1px'
              }}
              data-testid={`bundle-${bundle.type}`}
            >
              {bundle.type === '10' && (
                <div 
                  className="absolute -top-3 left-6 px-3 py-1 text-xs font-bold rounded-full flex items-center gap-1"
                  style={{ backgroundColor: '#C49603', color: '#FFFFFF' }}
                >
                  <Star className="h-3 w-3" fill="#FFFFFF" />
                  BEST VALUE
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-3xl font-bold" style={{ color: '#245451' }}>
                    {bundle.credits} {bundle.credits === 1 ? 'Credit' : 'Credits'}
                  </h3>
                  <p className="text-base mt-2" style={{ color: '#64748B' }}>
                    ${bundle.price_per_credit.toFixed(2)} per credit
                  </p>
                  {bundle.savings && (
                    <p className="text-sm mt-1 font-semibold" style={{ color: '#16A34A' }}>
                      ✓ Save ${bundle.savings.toFixed(2)}
                    </p>
                  )}
                </div>
                <div className="text-right flex flex-col items-end">
                  <div className="text-4xl font-bold" style={{ color: '#245451' }}>
                    ${bundle.price.toFixed(2)}
                  </div>
                  <Button
                    onClick={() => handlePurchase(bundle.type)}
                    disabled={loading}
                    size="lg"
                    className="mt-4 px-8 h-12 text-base font-semibold"
                    style={{ 
                      backgroundColor: '#C49603',
                      color: '#FFFFFF'
                    }}
                    data-testid={`buy-bundle-${bundle.type}-btn`}
                  >
                    {loading && selectedBundle === bundle.type ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Buy Now'
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <div className="text-sm text-center py-4" style={{ color: '#94A3B8', borderTop: '1px solid #E2E8F0' }}>
          <p>💳 Secure payment powered by Stripe (Demo Mode)</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BuyCreditsModal;